/**
 * Phone Number Formatting Utilities
 *
 * Formats phone numbers from E.164 format to human-readable format
 */

/**
 * Format phone number from E.164 to display format
 *
 * @param phone - Phone number in E.164 format (e.g., "+15551234567")
 * @returns Formatted phone number (e.g., "+1 (555) 123-4567")
 *
 * @example
 * formatPhoneNumber("+15551234567") // "+1 (555) 123-4567"
 * formatPhoneNumber("+441234567890") // "+44 1234 567890"
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return 'Not provided';

  // Debug logging
  console.log('[Phone Formatter] Input:', phone, 'Type:', typeof phone, 'Length:', phone.length);

  // Remove any non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  console.log('[Phone Formatter] Cleaned:', cleaned, 'Length:', cleaned.length);

  // Normalize: Add + prefix if missing
  if (!cleaned.startsWith('+')) {
    console.log('[Phone Formatter] No + prefix, adding it');
    cleaned = '+' + cleaned;
  }

  // Extract country code and number
  // Common patterns: +1 (North America), +44 (UK), +61 (Australia), etc.

  // +1 (North America) - Format: +1 (555) 123-4567
  if (cleaned.startsWith('+1')) {
    console.log('[Phone Formatter] Detected +1 number');
    // Handle both 11-digit (10 digit number) and 12-digit formats
    if (cleaned.length === 12) {
      const areaCode = cleaned.substring(2, 5);
      const prefix = cleaned.substring(5, 8);
      const lineNumber = cleaned.substring(8, 12);
      const formatted = `+1 (${areaCode}) ${prefix}-${lineNumber}`;
      console.log('[Phone Formatter] Formatted (12):', formatted);
      return formatted;
    } else if (cleaned.length === 11) {
      // Sometimes it's stored as +1 followed by 10 digits
      const areaCode = cleaned.substring(1, 4);
      const prefix = cleaned.substring(4, 7);
      const lineNumber = cleaned.substring(7, 11);
      const formatted = `+1 (${areaCode}) ${prefix}-${lineNumber}`;
      console.log('[Phone Formatter] Formatted (11):', formatted);
      return formatted;
    }
  }

  // +44 (UK) - Format: +44 1234 567890
  if (cleaned.startsWith('+44') && cleaned.length >= 12) {
    const localNumber = cleaned.substring(3);
    return `+44 ${localNumber.substring(0, 4)} ${localNumber.substring(4)}`;
  }

  // +61 (Australia) - Format: +61 412 345 678
  if (cleaned.startsWith('+61') && cleaned.length >= 11) {
    const localNumber = cleaned.substring(3);
    return `+61 ${localNumber.substring(0, 3)} ${localNumber.substring(3, 6)} ${localNumber.substring(6)}`;
  }

  // +81 (Japan) - Format: +81 90-1234-5678
  if (cleaned.startsWith('+81') && cleaned.length >= 12) {
    const localNumber = cleaned.substring(3);
    return `+81 ${localNumber.substring(0, 2)}-${localNumber.substring(2, 6)}-${localNumber.substring(6)}`;
  }

  // Generic format for other countries: +XX XXXXX...
  const countryCodeMatch = cleaned.match(/^\+(\d{1,4})/);
  if (countryCodeMatch) {
    const countryCode = countryCodeMatch[1];
    const localNumber = cleaned.substring(countryCode.length + 1);
    // Insert space every 3-4 digits
    const formatted = localNumber.replace(/(\d{3,4})(?=\d)/g, '$1 ');
    return `+${countryCode} ${formatted}`;
  }

  // If nothing matches, return original
  console.log('[Phone Formatter] No match, returning original');
  return phone;
}
